import { createContext, useContext, useState } from "react";
import { createStore, useStore } from "zustand";
import { combine } from "zustand/middleware";

import { isCompactViewport } from "./EventsListToggle";

const createCompetitionStore = (competitionSelectorOpen: boolean) =>
  createStore(
    combine(
      {
        selectedCategoryUuid: null as string | null,
        competitionSelectorOpen,
        categoryEditorIsDirty: false,
      },
      (set) => ({
        setSelectedCategoryUuid: (selectedCategoryUuid: string | null) =>
          set({ selectedCategoryUuid }),
        setCompetitionSelectorOpen: (competitionSelectorOpen: boolean) =>
          set({ competitionSelectorOpen }),
        setCategoryEditorDirty: (categoryEditorIsDirty: boolean) => set({ categoryEditorIsDirty }),
      }),
    ),
  );

type Context = ReturnType<typeof createCompetitionStore>;

const CompetitionContext = createContext<Context | undefined>(undefined);

export const CompetitionContextProvider = ({
  children,
  collapseSelectorOnCompact = false,
}: {
  children: React.ReactNode;
  /**
   * Start with the events list collapsed on narrow screens. Set this where an event is already
   * selected; on the events index the list is the only content, so it must stay open.
   */
  collapseSelectorOnCompact?: boolean;
}) => {
  const [store] = useState(() =>
    createCompetitionStore(!(collapseSelectorOnCompact && isCompactViewport())),
  );
  return <CompetitionContext.Provider value={store}>{children}</CompetitionContext.Provider>;
};

export const useCompetitionContext = () => {
  const store = useContext(CompetitionContext);

  if (!store) {
    throw new Error("useCompetitionContext must be used within a CompetitionContextProvider");
  }

  return useStore(store);
};
